import { motionAllowed } from '../../lib/motion';
import { pointAt } from './rich-field';

/** At most this many words fly, spread over the text in sight, or over the passage selected. */
const WORDS = 16;
/** Each word lifts off, arcs over, and fades as it lands, a beat after the one before. */
const FLIGHT_MS = 1_000;
const STAGGER_MS = 38;
/** While a word is in the air, its place dims, from its lift-off to past its landing. */
const LIFT_MS = 150;
const AWAY_MS = 1_100;
/** The versions' cards come in as the first words land, one after another. */
const ARRIVE_MS = 650;
const CARD_STAGGER_MS = 70;
/** A word flying into the draft is on its own place there this far through its flight. */
const LANDED = 0.9;
/** How far around where it should be a word looks for itself in the draft. */
const NEAR = 8;
/** The app's entrances: quick to arrive, then a long, soft settle (--ease-out). */
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
/** A flying word keeps clear of the window's edges. */
const EDGE = 8;

interface Word {
  readonly node: Text;
  readonly start: number;
  readonly end: number;
  readonly text: string;
  /** Its place among the words found with it. */
  readonly index: number;
}

function rangeOf(word: Word): Range {
  const range = document.createRange();
  range.setStart(word.node, word.start);
  range.setEnd(word.node, word.end);
  return range;
}

/**
 * The words in `root`, in order, or those within `within`: what reads, leaving out the draft's
 * hidden formatting and its widgets.
 */
function wordsOf(root: HTMLElement, within: Range | null = null): Word[] {
  const words: Word[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node as Text;
    if (text.parentElement?.closest('[data-hidden], [data-widget]')) continue;
    for (const match of text.data.matchAll(/\S+/gu)) {
      const start = match.index;
      const end = start + match[0].length;
      if (within && !(within.isPointInRange(text, start) && within.isPointInRange(text, end)))
        continue;
      words.push({ node: text, start, end, text: match[0], index: words.length });
    }
  }
  return words;
}

/** Those of `words` wholly in sight in `root`, which may scroll, and in the window. */
function inSight(words: readonly Word[], root: HTMLElement): Word[] {
  const box = root.getBoundingClientRect();
  const top = Math.max(box.top, 0);
  const bottom = Math.min(box.bottom, window.innerHeight);
  const nodes = new Map<Text, DOMRect>();
  const seen: Word[] = [];
  for (const word of words) {
    let lines = nodes.get(word.node);
    if (!lines) {
      const whole = document.createRange();
      whole.selectNodeContents(word.node);
      lines = whole.getBoundingClientRect();
      nodes.set(word.node, lines);
    }
    if (lines.bottom < top) continue;
    if (lines.top > bottom) break;
    const place = rangeOf(word).getBoundingClientRect();
    if (place.width > 0 && place.top >= top && place.bottom <= bottom) seen.push(word);
  }
  return seen;
}

/** Up to WORDS of `words`, spread evenly over them. */
function spread(words: readonly Word[]): Word[] {
  const step = Math.max(1, words.length / WORDS);
  return Array.from(
    { length: Math.min(WORDS, words.length) },
    (_, index) => words[Math.floor(index * step)],
  ).filter((word): word is Word => word !== undefined);
}

/** The passage selected in `root`, if any. */
function selectedIn(root: HTMLElement): Range | null {
  const selection = getSelection();
  return selection && !selection.isCollapsed && root.contains(selection.anchorNode)
    ? selection.getRangeAt(0)
    : null;
}

/** A copy of `word`, in its own type, lit, over its place, ready to fly. */
function flyerFor(word: Word, place: DOMRect): HTMLElement {
  const flyer = document.createElement('span');
  flyer.className = 'word-flight';
  flyer.textContent = word.text;
  const parent = word.node.parentElement;
  if (parent) {
    const style = getComputedStyle(parent);
    flyer.style.font = style.font;
    flyer.style.letterSpacing = style.letterSpacing;
  }
  flyer.style.left = `${place.left}px`;
  flyer.style.top = `${place.top}px`;
  flyer.style.lineHeight = `${place.height}px`;
  document.body.append(flyer);
  return flyer;
}

/** Sends `flyer` on its way after `delay`, and takes it away once it's landed. */
function fly(flyer: HTMLElement, keyframes: Keyframe[], delay: number): void {
  const flight = flyer.animate(keyframes, { duration: FLIGHT_MS, delay, fill: 'both' });
  const land = () => {
    flyer.remove();
  };
  flight.addEventListener('finish', land);
  flight.addEventListener('cancel', land);
}

/** The lift and the arc of a flight `tx` across and `ty` down, `size` the size it lands at. */
function arc(tx: number, ty: number, size = 0.75): Keyframe[] {
  const lift = -12 - Math.random() * 10;
  return [
    { transform: 'translate(0, 0) scale(1)', opacity: 0, easing: EASE_OUT },
    {
      transform: `translate(0, ${lift}px) scale(1.08)`,
      opacity: 1,
      offset: 0.2,
      easing: 'ease-in',
    },
    {
      transform: `translate(${tx * 0.5}px, ${Math.min(lift, ty * 0.5) - 26}px) scale(${(1.15 + size) / 2})`,
      opacity: 1,
      offset: 0.6,
      easing: 'ease-out',
    },
  ];
}

/**
 * Marks text for a while with `name`'s style (input-panel.css), where the browser can mark text
 * without changing it. The marks go once the last of them ends, unless a later flight has its own.
 */
function marking(name: string) {
  const registry =
    typeof Highlight === 'function' ? (CSS as Partial<typeof CSS>).highlights : undefined;
  const marks = registry ? new Highlight() : undefined;
  if (registry && marks) registry.set(name, marks);
  let last = 0;
  return {
    mark(range: Range, from: number, to: number) {
      if (!marks) return;
      last = Math.max(last, to);
      if (from > 0) setTimeout(() => marks.add(range), from);
      else marks.add(range);
      setTimeout(() => marks.delete(range), to);
    },
    end() {
      if (registry && marks)
        setTimeout(() => {
          if (registry.get(name) === marks) registry.delete(name);
        }, last);
    },
  };
}

/** Where a word lands in `card`: somewhere in its body, kept within the window. */
function landing(card: DOMRect): { x: number; y: number } {
  const x = card.left + 16 + Math.random() * Math.max(10, card.width - 80);
  const y = card.top + 40 + Math.random() * Math.max(10, card.height - 60);
  return {
    x: Math.min(Math.max(x, EDGE), window.innerWidth - EDGE * 3),
    y: Math.min(Math.max(y, EDGE), window.innerHeight - EDGE * 3),
  };
}

/**
 * Words take flight, as a rewrite starts: words lift off the draft in sight (or off the passage
 * selected), in the accent's light, and fly in arcs to the versions' `cards`, fading as they land.
 * Their places in the draft dim while they're away. Cards new to the page, as they `enter`, come in
 * as the first words land, one after another; each version then arrives word by word, as it
 * always does (MarkdownText).
 */
export function playWordFlight(
  field: HTMLElement | null,
  cards: readonly HTMLElement[],
  enter = true,
): void {
  if (!field || !motionAllowed()) return;
  // Cards laid out somewhere, even out of sight: words fly toward those below to the window's edge.
  const shown = cards.filter((card) => {
    const place = card.getBoundingClientRect();
    return place.width > 0 && place.height > 0;
  });
  if (!shown.length) return;
  const words = spread(inSight(wordsOf(field, selectedIn(field)), field));
  const places = shown.map((card) => card.getBoundingClientRect());

  if (enter)
    shown.forEach((card, index) => {
      card.animate(
        [
          { opacity: 0, transform: 'scale(0.96)' },
          { opacity: 1, transform: 'none' },
        ],
        {
          duration: 420,
          delay: (words.length ? ARRIVE_MS : 0) + index * CARD_STAGGER_MS,
          easing: EASE_OUT,
          fill: 'backwards',
        },
      );
    });

  const away = marking('word-flight');
  words.forEach((word, index) => {
    const place = places[index % places.length];
    if (!place) return;
    const range = rangeOf(word);
    const from = range.getBoundingClientRect();
    const to = landing(place);
    const tx = to.x - from.left;
    const ty = to.y - from.top;
    const delay = index * STAGGER_MS;
    fly(
      flyerFor(word, from),
      [...arc(tx, ty), { transform: `translate(${tx}px, ${ty}px) scale(0.75)`, opacity: 0 }],
      delay,
    );
    away.mark(range, delay + LIFT_MS, delay + AWAY_MS);
  });
  away.end();
}

/**
 * Each of `words` (from a text of `count` words) with its place in `targets`, the same text: the
 * same word, at about the same place, each after the one before.
 */
function pair(words: readonly Word[], count: number, targets: readonly Word[]): [Word, Word][] {
  const pairs: [Word, Word][] = [];
  let next = 0;
  for (const word of words) {
    const guess = Math.max(next, Math.round((word.index * targets.length) / Math.max(count, 1)));
    const near: number[] = [];
    for (let step = 0; step <= NEAR; step++) near.push(guess + step, guess - step);
    const found =
      near
        .filter((index) => index >= next && index < targets.length)
        .map((index) => targets[index])
        .find((target) => target?.text === word.text) ??
      targets[Math.min(guess, targets.length - 1)];
    if (!found || found.index < next) continue;
    pairs.push([word, found]);
    next = found.index + 1;
  }
  return pairs;
}

/**
 * A version taken to edit takes flight the other way: words lift off its `card`, in sight, and
 * each flies to its own place in the draft `field`, where the version now is, from `start` to
 * `end` in it. The version's text there shows from its start as they land, and their places on
 * the card dim while they're away.
 */
export function playEditFlight(
  card: HTMLElement | null,
  field: HTMLElement | null,
  start: number,
  end: number,
): void {
  if (!card || !field || !motionAllowed()) return;
  const version = document.createRange();
  version.setStart(...pointAt(field, start));
  version.setEnd(...pointAt(field, end));
  const sources = wordsOf(card);
  const targets = wordsOf(field, version);
  const flights = pair(spread(inSight(sources, card)), sources.length, targets);
  if (!flights.length) return;
  const sight = field.getBoundingClientRect();

  const away = marking('word-flight');
  const hidden = marking('word-flight-arriving');
  let shown: [Node, number] = [version.startContainer, version.startOffset];
  let lastLanding = 0;
  flights.forEach(([word, target], index) => {
    const source = rangeOf(word);
    const from = source.getBoundingClientRect();
    const goal = rangeOf(target).getBoundingClientRect();
    // A place scrolled out of the draft's sight: the nearest edge of what can be seen.
    const top = Math.min(
      Math.max(goal.top, sight.top),
      Math.max(sight.top, sight.bottom - goal.height),
    );
    const tx = goal.left - from.left;
    const ty = top - from.top;
    const size = from.height ? goal.height / from.height : 1;
    const delay = index * STAGGER_MS;
    const flyer = flyerFor(word, from);
    flyer.style.transformOrigin = '0 0';
    const landed = `translate(${tx}px, ${ty}px) scale(${size})`;
    fly(
      flyer,
      [
        ...arc(tx, ty, size),
        { transform: landed, opacity: 1, offset: LANDED },
        { transform: landed, opacity: 0 },
      ],
      delay,
    );
    away.mark(source, delay + LIFT_MS, delay + AWAY_MS);
    // The version's text up to this word shows as it lands.
    lastLanding = delay + FLIGHT_MS * LANDED;
    const coming = document.createRange();
    coming.setStart(...shown);
    coming.setEnd(target.node, target.end);
    hidden.mark(coming, 0, lastLanding);
    shown = [target.node, target.end];
  });
  // And the rest, as the last lands.
  const rest = document.createRange();
  rest.setStart(...shown);
  rest.setEnd(version.endContainer, version.endOffset);
  hidden.mark(rest, 0, lastLanding);
  away.end();
  hidden.end();
}
